import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { fetchAirlinesApi } from 'apis/authServices';

interface Airline {
  id: string | number;
  airlineName: string;
}

interface AirlinePickerProps {
  selectedAirlineIds: number[];
  onChange: (airlineIds: number[]) => void;
  placeholder?: string;
  maxSelections?: number;
  showChips?: boolean;
  disabled?: boolean;
  label?: string;
  error?: string;
  className?: string;
  showConfirmButton?: boolean;
  confirmButtonText?: string;
  onConfirm?: () => void;
  isRequired?: boolean
  initialAirlineNames?: string[]; // Array of airline names corresponding to selectedAirlineIds
}

/**
 * Reusable Airline Picker Component
 * Multi-select dropdown with search, chips, and bulk actions
 * 
 * @param selectedAirlineIds - Array of selected airline IDs
 * @param onChange - Callback when selection changes
 * @param placeholder - Input placeholder text
 * @param maxSelections - Maximum number of airlines that can be selected
 * @param showChips - Show selected airlines as chips below input (default: true)
 * @param disabled - Disable the picker
 * @param label - Label text for the input
 * @param error - Error message to display
 * @param className - Additional CSS classes
 * @param showConfirmButton - Show confirmation button in dropdown footer (default: true)
 * @param confirmButtonText - Custom text for confirm button (default: "Confirm Selection")
 * @param onConfirm - Callback when confirmation button is clicked
 */
const AirlinePicker: React.FC<AirlinePickerProps> = ({
  selectedAirlineIds,
  onChange,
  placeholder = 'Search airlines...',
  maxSelections,
  showChips = true,
  disabled = false,
  label,
  error,
  className = '',
  showConfirmButton = true,
  confirmButtonText = 'Confirm Selection',
  onConfirm,
  isRequired,
  initialAirlineNames = [],
}) => {
  const [airlines, setAirlines] = useState<Airline[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch airlines from API
  const fetchAirlines = useCallback(async (query = '') => {
    try {
      setLoading(true);
      const response = await fetchAirlinesApi(query);
      const fetchedAirlines = Array.isArray(response) ? response : [];
      setAirlines(fetchedAirlines);
    } catch (error) {
      console.error('Error fetching airlines:', error);
      setAirlines([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch airlines when dropdown opens
  useEffect(() => {
    if (showDropdown && airlines.length === 0) {
      fetchAirlines();
    }
  }, [showDropdown, airlines.length, fetchAirlines]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    fetchAirlines(value);
  };

  // Toggle airline selection
  const handleAirlineToggle = (airlineId: number) => {
    const isSelected = selectedAirlineIds.includes(airlineId);
    let newSelection: number[];

    if (isSelected) {
      newSelection = selectedAirlineIds.filter(id => id !== airlineId);
    } else {
      // Check max selections
      if (maxSelections && selectedAirlineIds.length >= maxSelections) {
        return; // Don't allow more selections
      }
      newSelection = [...selectedAirlineIds, airlineId];
    }

    onChange(newSelection);
  };

  // Select all airlines
  const handleSelectAll = () => {
    const allIds = airlines.map(a => parseInt(String(a.id), 10));
    const limitedIds = maxSelections ? allIds.slice(0, maxSelections) : allIds;
    onChange(limitedIds);
  };

  // Clear all selections
  const handleClearAll = () => {
    onChange([]);
  };

  // Remove single airline chip
  const handleRemoveChip = (airlineId: number) => {
    onChange(selectedAirlineIds.filter(id => id !== airlineId));
  };

  // Create a map of airline IDs to names for quick lookup
  const airlineNameMap = useMemo(() => {
    const map = new Map<number, string>();
    
    // First, create map from initialAirlineNames if provided
    if (initialAirlineNames && selectedAirlineIds.length === initialAirlineNames.length) {
      selectedAirlineIds.forEach((id, index) => {
        if (initialAirlineNames[index]) {
          map.set(id, initialAirlineNames[index]);
        }
      });
    }
    
    // Then, add any airlines fetched from API (this will override if there's a match)
    airlines.forEach(airline => {
      const airlineId = parseInt(String(airline.id), 10);
      map.set(airlineId, airline.airlineName);
    });
    
    return map;
  }, [initialAirlineNames, selectedAirlineIds, airlines]);

  // Get airline name by ID
  const getAirlineName = (id: number): string => {
    // First try to get from the map
    if (airlineNameMap.has(id)) {
      return airlineNameMap.get(id)!;
    }
    // Fallback to ID if name not found
    return `Airline ${id}`;
  };

  // Filter airlines based on search
  const filteredAirlines = useMemo(() => {
    if (!searchQuery) return airlines;
    return airlines.filter(airline =>
      airline.airlineName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [airlines, searchQuery]);

  // Get display value for input field
  const inputDisplayValue = useMemo(() => {
    if (showDropdown || searchQuery) {
      return searchQuery;
    }
    // When dropdown is closed, show selected airline names
    if (selectedAirlineIds.length > 0) {
      const names = selectedAirlineIds.map(id => {
        if (airlineNameMap.has(id)) {
          return airlineNameMap.get(id)!;
        }
        return `Airline ${id}`;
      });
      return names.join(', ');
    }
    return '';
  }, [showDropdown, searchQuery, selectedAirlineIds, airlineNameMap]);

  // Handle confirm button click
  const handleConfirm = useCallback(() => {
    setShowDropdown(false);
    setSearchQuery('');
    onConfirm?.();
  }, [onConfirm]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Label */}
      {label && (
        <label className="block text-xs text-gray-500 mb-2">
          {label}
        {isRequired && <span className="text-red-500 ml-1">*</span>}
          {maxSelections && (
            <span className="ml-1 text-gray-400">
              (Max: {maxSelections})
            </span>
          )}
        </label>
      )}

      {/* Search Input */}
      <input
        ref={inputRef}
        type="text"
        value={inputDisplayValue}
        onChange={handleSearchChange}
        onFocus={() => {
          setShowDropdown(true);
          setSearchQuery('');
          if (airlines.length === 0) {
            fetchAirlines();
          }
        }}
        placeholder={selectedAirlineIds.length > 0 ? '' : placeholder}
        disabled={disabled}
        className={`block w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${error ? 'border-2 border-red-500' : 'border-0'}`}
        autoComplete="off"
        readOnly={!showDropdown && selectedAirlineIds.length > 0}
      />

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}

      {/* Dropdown */}
      {showDropdown && !disabled && (
        <div className="absolute w-full bg-white shadow-lg rounded-lg mt-1 z-10">
          {/* Header with stats and bulk actions */}
          <div className="p-3 bg-gray-50 flex justify-between items-center sticky top-0 z-10">
            <span className="text-xs text-gray-600 font-medium">
              {selectedAirlineIds.length} of {filteredAirlines.length} selected
              {maxSelections && ` (Max: {maxSelections})`}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={maxSelections ? selectedAirlineIds.length >= maxSelections : false}
                className="text-xs px-2 py-1 bg-[#FFD700] text-white rounded hover:bg-[#E6C200] transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs px-2 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Airline List */}
          <div className="max-h-60 overflow-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <p className="text-gray-500 text-sm mt-2">Loading airlines...</p>
              </div>
            ) : filteredAirlines.length > 0 ? (
              <ul>
                {filteredAirlines.map((airline) => {
                  const airlineId = parseInt(String(airline.id), 10);
                  const isSelected = selectedAirlineIds.includes(airlineId);
                  const isDisabled = !isSelected && maxSelections && selectedAirlineIds.length >= maxSelections;

                  return (
                    <li
                      key={airline.id}
                      className={`p-3 cursor-pointer hover:bg-blue-50 flex items-center border-b border-gray-100 last:border-0 transition ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => !isDisabled && handleAirlineToggle(airlineId)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => !isDisabled && handleAirlineToggle(airlineId)}
                        disabled={!!isDisabled}
                        className="mr-3 h-4 w-4 text-[#FFD700] rounded cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="text-sm text-gray-700">{airline.airlineName}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="p-8 text-center">
                <p className="text-gray-400 text-sm">
                  {searchQuery ? 'No airlines found' : 'No airlines available'}
                </p>
              </div>
            )}
          </div>

          {/* Footer with Confirm Button */}
          {showConfirmButton && (
            <div className="p-2 border-t border-gray-200 bg-gray-50 sticky bottom-0">
              <button
                type="button"
                onClick={handleConfirm}
                className="w-full px-3 py-2 bg-[#FFD700] text-white text-sm rounded hover:bg-[#E6C200] transition"
              >
                {confirmButtonText}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Selected Airlines Chips */}
      {showChips && selectedAirlineIds.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedAirlineIds.map((id, index) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs"
            >
              {getAirlineName(id)}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveChip(id)}
                  className="hover:bg-blue-200 rounded-full p-0.5 transition"
                  aria-label={`Remove ${getAirlineName(id)}`}
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default AirlinePicker;

